import { Component } from '@theme/component';

/** Keeps the mobile purchase control aligned with the existing product form. */
class ALGStickyPurchase extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.setup();
  }
  updatedCallback() {
    super.updatedCallback();
    this.cleanup();
    this.setup();
  }
  setup() {
    this.pending = false;
    this.root = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
    if (!this.root) return;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.media = matchMedia('(max-width: 749px)');
    this.buy = this.querySelector('[data-alg-buy]');
    this.optionsButton = this.querySelector('[data-alg-options]');
    this.price = this.querySelector('[data-alg-price]');
    this.onResize = () => this.queueSync();
    this.media.addEventListener('change', this.onResize);
    window.addEventListener('scroll', () => this.queueSync(), { passive: true, signal: this.abort.signal });
    this.buy.addEventListener('click', () => {
      const button = this.nativeButton();
      if (button && !button.disabled) button.click();
      else this.chooseOptions();
    }, options);
    this.optionsButton.addEventListener('click', () => this.chooseOptions(), options);
    this.root.addEventListener('variant:selected', () => {
      this.pending = true;
      this.sync();
    }, options);
    this.root.addEventListener('variant:update', () => {
      this.pending = false;
      this.queueSync();
    }, options);
    this.root.addEventListener('change', () => this.queueSync(), options);
    this.root.addEventListener('submit', () => this.queueSync(), options);
    this.observer = new MutationObserver(() => this.queueSync());
    const details = this.root.querySelector('.product-details');
    if (details) this.observer.observe(details, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'aria-busy', 'class', 'value'] });
    this.intersection = new IntersectionObserver(() => this.queueSync(), { threshold: 0 });
    const button = this.nativeButton();
    if (button) { this.observedButton = button; this.intersection.observe(button); }
    document.addEventListener('focusin', () => this.queueSync(), options);
    document.addEventListener('focusout', () => this.queueSync(), options);
    this.sync();
  }
  disconnectedCallback() {
    this.cleanup();
    super.disconnectedCallback();
  }
  cleanup() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.intersection?.disconnect();
    this.media?.removeEventListener('change', this.onResize);
    cancelAnimationFrame(this.frame);
    this.frame = undefined;
    document.body.classList.remove('alg-has-sticky-purchase');
  }
  queueSync() {
    if (this.frame || !this.isConnected) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = undefined;
      this.sync();
    });
  }
  nativeButton() {
    return this.root.querySelector('product-form-component button[name="add"]');
  }
  chooseOptions() {
    const picker = this.root.querySelector('variant-picker');
    const target = picker?.querySelector('input:checked, select, input:not([disabled])') || this.nativeButton();
    (picker || target)?.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    target?.focus({ preventScroll: true });
  }
  sync() {
    if (!this.isConnected || !this.root) return;
    const native = this.nativeButton();
    const form = native?.closest('form');
    if (!native || !form) {
      this.hidden = true;
      document.body.classList.remove('alg-has-sticky-purchase');
      return;
    }
    const rect = native.getBoundingClientRect();
    if (native !== this.observedButton) {
      this.intersection.disconnect();
      this.intersection.observe(native);
      this.observedButton = native;
    }
    const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || '') && !['radio', 'checkbox', 'hidden'].includes(document.activeElement?.type);
    const visible = this.media.matches && rect.bottom <= 0 && !typing;
    this.hidden = !visible;
    document.body.classList.toggle('alg-has-sticky-purchase', visible);
    const sourcePrice = this.root.querySelector('.product-details product-price .price');
    if (sourcePrice) this.price.textContent = sourcePrice.textContent.trim();
    const picker = this.root.querySelector('variant-picker');
    const values = picker ? [...picker.querySelectorAll('input[type="radio"]:checked, select')].map(input => input.value).filter(Boolean) : [];
    this.optionsButton.textContent = values.length ? `${this.dataset.sizeLabel}: ${values.join(' / ')}` : this.dataset.chooseLabel;
    this.optionsButton.hidden = !picker;
    this.buy.disabled = this.pending || native.disabled || !form.querySelector('input[name="id"]')?.value;
    const sourceLabel = native.querySelector('.add-to-cart-text__content')?.textContent.trim();
    this.buy.textContent = this.pending ? this.dataset.addingLabel : (sourceLabel || this.dataset.addLabel);
  }
}
if (!customElements.get('alg-sticky-purchase')) customElements.define('alg-sticky-purchase', ALGStickyPurchase);
